import { useQuery } from '@apollo/client'
import { Button, Input, X } from '@chain-zen/ui'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

import { AppCard } from '../../../../components/apps/app-card'
import { apps } from '../../../../components/apps/apps-data'
import { EnableAppCompletionModal } from '../../../../components/apps/modals/enable-app-completion-modal'
import { USER_ROUTES } from '../../../../constants/routes'
import { GET_APPS } from '../../../../data/queries/apps'
import { useAuth } from '../../../../hooks/use-auth'
import type { TWorkspaceApp } from '../../../../types/apps'
import { AppCategoriesEnum, AppTypeEnum } from '../../../../types/workspace'
import { useCarrierOnboarding } from '../_context/carrier-onboarding-context'

/**
 * The carrier can choose to enable different available ELD apps during onboarding
 */
export const CarrierELDApps = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { selectedWorkspace } = useAuth()
  const router = useRouter()

  const onOpen = () => {
    setIsOpen(true)
  }

  const onClose = () => {
    setIsOpen(false)
  }
  /** Get enabled apps */
  const { data: workspaceAppsData } = useQuery<{
    workspaceApps: TWorkspaceApp[]
  }>(GET_APPS, {
    context: {
      headers: {
        'ws-id': selectedWorkspace?.workspace.id || ''
      }
    },
    fetchPolicy: 'no-cache'
  })

  const workspaceApps = workspaceAppsData?.workspaceApps

  const { markStepAsCompleted } = useCarrierOnboarding()

  // Popular ELD apps priority order (most popular first)
  // These are shown first before alphabetical sorting
  const popularELDPriority: Partial<Record<AppTypeEnum, number>> = {
    [AppTypeEnum.GeoTab]: 1,
    [AppTypeEnum.Samsara]: 2,
    [AppTypeEnum.VerizonConnectFleetTelogis]: 3, // Verizon-Telogis
    [AppTypeEnum.VerizonConnectReveal]: 4, // Verizon-Reveal
    [AppTypeEnum.ATTFleetManagement]: 5, // ATT Fleet Management
    [AppTypeEnum.Motive]: 6
  } as const

  // Filter out apps that are not ELD apps
  const appsData = apps
    .filter((a) => a.categories.includes(AppCategoriesEnum.ELD))
    .filter((a) => a.appType !== AppTypeEnum.Orbcomm)
    .map((a) => {
      const workspaceAppOrUndefined = workspaceApps?.find((wa) => wa.appType === a.appType)
      // Check if this app type exists in workspaceApps AND is enabled
      const isEnabled = Boolean(workspaceApps?.some((wa) => wa.appType === a.appType && wa.enabled === true))

      return {
        ...a,
        isEnabled,
        workspaceApp: workspaceAppOrUndefined,
        workspaceID: workspaceAppOrUndefined?.workspaceID
      }
    })
    .sort((a, b) => {
      // 1. Sort by enabled status first (enabled apps first)
      if (a.isEnabled !== b.isEnabled) {
        return a.isEnabled ? -1 : 1
      }

      // 2. Within each enabled/disabled group, prioritize popular ELD apps
      const aPriority = popularELDPriority[a.appType]
      const bPriority = popularELDPriority[b.appType]

      // If both apps are in the popular list, sort by priority (lower number = higher priority)
      if (aPriority && bPriority) {
        return aPriority - bPriority
      }

      // If only one app is popular, it comes first
      if (aPriority && !bPriority) {
        return -1
      }
      if (!aPriority && bPriority) {
        return 1
      }

      // If neither app is in the popular list, sort alphabetically
      return a.appType.localeCompare(b.appType)
    })
    .filter((app) => {
      if (!search) return true

      const searchLower = search.toLowerCase()

      // Search in app name
      if (app.appName.toLowerCase().includes(searchLower)) return true

      // Search in searchMetadata if it exists
      if (app.searchMetadata) {
        return app.searchMetadata.some((metadata) => metadata.toLowerCase().includes(searchLower))
      }

      return false
    })

  /** After a user returns from an OAuth callback, we pass various params in the url which is used to indicate whether enabling was a success or failure */
  const searchParams = useSearchParams()
  const appParam = searchParams.get('app')
  const redirected = searchParams.get('redirected')
  const status = searchParams.get('status')
  const message = searchParams.get('message')

  useEffect(() => {
    if (redirected === 'true') {
      onOpen()
    }
  }, [onOpen, redirected])

  // Handler for skipping ELD setup
  const handleSkipEldSetup = () => {
    markStepAsCompleted('eld_setup')

    // If user is a Member, navigate directly to assigned-to-me page
    if (selectedWorkspace?.accessType === 'Member') {
      router.push(USER_ROUTES.SHIPMENTS)
    }
  }

  // Custom modal close handler that also marks the step as completed
  const handleModalClose = () => {
    onClose()
    if (status === 'true' || status === 'success') {
      markStepAsCompleted('eld_setup')

      // If user is a Member, navigate directly to assigned-to-me page
      if (selectedWorkspace?.accessType === 'Member') {
        router.push(USER_ROUTES.SHIPMENTS)
      }
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header Section */}
          <div className="text-center space-y-4">
            <div className="text-4xl mb-4">🛜</div>
            <h1 className="text-2xl font-bold">Connect your ELD application</h1>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Connect your ELD or GPS to become a preferred Chain carrier with priority access to offers and repeat
              business from verified brokers. Your login details are never shared. ELD or GPS connection prevents fraud
              and enables seamless load tracking.
            </p>
          </div>

          {/* Next Button Section - Show when apps are enabled */}
          {appsData.some((app) => app.isEnabled) && (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Enable another ELD provider or click "Next" to finish setting up your company
              </p>
              <Button variant="outline" onClick={handleSkipEldSetup}>
                Next
              </Button>
            </div>
          )}

          {/* Search Section */}
          <div className="flex justify-center">
            <div className="relative w-full max-w-md">
              <Input
                placeholder="Search ELD apps..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-10"
              />
              {Boolean(search) && (
                <X
                  className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={() => setSearch('')}
                />
              )}
            </div>
          </div>

          {/* Support Notice */}
          <div className="text-center py-4">
            <p className="text-muted-foreground">
              Don't see your ELD or GPS?{' '}
              <a
                href="https://help.trychain.com/en/articles/12845894-don-t-see-your-eld"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 font-medium underline underline-offset-4 decoration-1"
              >
                Follow instructions here
              </a>
            </p>
          </div>

          {/* Apps Grid */}
          <div className="flex flex-col gap-6 w-full h-full min-h-[400px]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 flex-1">
              {appsData.map((app) => (
                <AppCard key={app.id + app.appType} app={app} forCarrierOnboarding={true} />
              ))}
              {appsData.length === 0 && Boolean(search) && (
                <div className="col-span-full text-center py-8">
                  <div className="text-4xl mb-4">🔍</div>
                  <h3 className="text-lg font-medium mb-2">No ELD apps found</h3>
                  <p className="text-muted-foreground">
                    No ELD apps match your search "{search}". Try a different search term.
                  </p>
                </div>
              )}
            </div>
          </div>

          <EnableAppCompletionModal
            isOpen={isOpen}
            onClose={handleModalClose}
            redirectParams={{ app: appParam, message, redirected, status }}
          />
        </div>
      </div>
    </div>
  )
}
